import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtTokenModel, AccountService, LogService, AccountModel, RoleProfiles } from '@nstep-common/core';
import { toast } from '@nstep-common/utils';
import { difference } from 'lodash';
import { PermissionsByRoute } from 'projects/common/core/other/permissions';
import { SessionService } from 'projects/common/core/services/auth/session.service';
import { EMPTY, Observable, catchError, finalize, of, tap } from 'rxjs';

const AUTO_LOGOUT_MINUTES = 30;
const AUTO_LOGOUT_WARNING_SECONDS = 30;
const TOKEN_REFRESH_BUFFER_MILLISECONDS = 50_000;

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	get jwt(): JwtTokenModel | null {
		return this.sessionService.getJwt(true);
	}

	private isLoggingOut = false;
	private refreshTimer: ReturnType<typeof setTimeout> | null = null;
	private autoLogoutTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(private logger: LogService,
		private accountService: AccountService,
		private sessionService: SessionService,
		private router: Router,
		private permissionsByRoute: PermissionsByRoute) {
		const jwt = this.jwt;
		const accessToken = this.sessionService.getAccessToken();

		if (!jwt && accessToken) {
			setTimeout(() => {
				if (this.sessionService.getJwt(false)?.impersonatedBy) {
					this.logOut();
				}
				else {
					this.refreshSession()
						.pipe(finalize(() => this.keepSessionAlive()))
						.subscribe();
				}
			});
		}
		else if (!jwt) {
			this.sessionService.clear();
		}

		this.updateLastRequest();
		this.keepSessionAlive();
		this.startAutoLogout();
	}

	updateLastRequest(): void {
		this.sessionService.set('lastRequestTime', Date.now().toString());
		$('.ui.toast.auto-logout-extend').toast('destroy');
	}

	private startAutoLogout(): void {
		if (this.autoLogoutTimer !== null) {
			clearTimeout(this.autoLogoutTimer);
			this.autoLogoutTimer = null;
		}

		let remainingLogoutMinutes = AUTO_LOGOUT_MINUTES;
		let remainingWarningSeconds = AUTO_LOGOUT_MINUTES * 60 - AUTO_LOGOUT_WARNING_SECONDS;
		const jwt = this.jwt;

		if (!jwt) {
			return;
		}

		const lastRequestTime = Number(this.sessionService.get('lastRequestTime'));
		if (!Number.isFinite(lastRequestTime)) {
			this.logOut();
			toast('', 'Your session has expired.', 'orange');
			return;
		}

		const elapsedMilliseconds = Math.max(0, Date.now() - lastRequestTime);
		remainingLogoutMinutes -= Math.min(elapsedMilliseconds / (60 * 1000), AUTO_LOGOUT_MINUTES);
		remainingWarningSeconds -= Math.min(elapsedMilliseconds / 1000, AUTO_LOGOUT_MINUTES * 60);

		if (remainingLogoutMinutes <= 0) {
			this.logOut();
			toast('', 'Your session has expired.', 'orange');
			return;
		}

		if (remainingWarningSeconds <= 0) {
			if (!$('.auto-logout-extend').length) {
				$.toast({
					message: 'Your session is about to expire and you will be logged out, do you want to extend it?',
					displayTime: AUTO_LOGOUT_WARNING_SECONDS * 1000,
					position: 'bottom right',
					classActions: 'bottom attached',
					showProgress: 'bottom',
					actions: [{
						text: 'Yes',
						class: 'green',
						click: () => this.updateLastRequest()
					}]
				});
			}
			this.scheduleAutoLogout(remainingLogoutMinutes, remainingWarningSeconds);
			return;
		}

		// We do this to prevent lingering toasts from other browser tabs.
		$('.ui.toast.auto-logout-extend').toast('destroy');
		this.scheduleAutoLogout(remainingLogoutMinutes, remainingWarningSeconds);
	}

	private scheduleAutoLogout(
		remainingLogoutMinutes: number,
		remainingWarningSeconds: number
	): void {
		const warningActive = remainingWarningSeconds <= 0;
		const secondsUntilNextCheck = warningActive
			? remainingLogoutMinutes * 60
			: Math.min(remainingLogoutMinutes * 60, remainingWarningSeconds);
		const rescheduleSeconds = Math.max(1, secondsUntilNextCheck);

		this.autoLogoutTimer = setTimeout(
			() => this.startAutoLogout(),
			rescheduleSeconds * 1000
		);
	}

	private keepSessionAlive(): void {
		if (this.refreshTimer !== null) {
			clearTimeout(this.refreshTimer);
			this.refreshTimer = null;
		}

		const jwt = this.jwt;
		if (!jwt) {
			return;
		}

		const millisecondsUntilExpiry = jwt.exp * 1000 - Date.now();
		if (!Number.isFinite(millisecondsUntilExpiry)) {
			this.logOut();
			return;
		}

		if (jwt.impersonatedBy) {
			this.refreshTimer = setTimeout(() => this.logOut(), Math.max(0, millisecondsUntilExpiry));
			return;
		}

		const refreshDelayMilliseconds = Math.max(0, millisecondsUntilExpiry - TOKEN_REFRESH_BUFFER_MILLISECONDS);

		// Refresh before expiry to allow time for the request to complete.
		this.refreshTimer = setTimeout(() => {
			this.refreshSession()
				.pipe(finalize(() => this.keepSessionAlive()))
				.subscribe();
		}, refreshDelayMilliseconds);
	}

	logIn(model: AccountModel, impersonated = false): JwtTokenModel | null {
		if (!model.accessToken) {
			return null;
		}

		if (impersonated) {
			this.sessionService.backup();
		}
		else {
			this.sessionService.restore();
		}
		
		this.sessionService.setAccessToken(model.accessToken);
		this.updateLastRequest();
		this.keepSessionAlive();
		this.startAutoLogout();

		if (this.jwt) {
			this.logger.log(`User ${this.jwt.nameIdentifier} successfully logged in.`);
		}

		return this.jwt;
	}

	logOut(): void {
		if (this.autoLogoutTimer !== null) {
			clearTimeout(this.autoLogoutTimer);
			this.autoLogoutTimer = null;
		}

		if (this.refreshTimer !== null) {
			clearTimeout(this.refreshTimer);
			this.refreshTimer = null;
		}

		// Prevent multiple logouts while one is in progress
		if (this.isLoggingOut) {
			return;
		}

		const jwt = this.jwt;
		if (!jwt) {
			if (this.sessionService.getAccessToken()) {
				this.redirectToLogin(true);
			}

			return;
		}

		this.isLoggingOut = true;

		const isImpersonated = !!jwt.impersonatedBy;

		if (!isImpersonated) {
			this.accountService.logOut().subscribe();
		}
		else {
			this.sessionService.restore();
		}

		this.redirectToLogin(!isImpersonated);
		this.isLoggingOut = false;
	}

	hasAccess(...permissions: string[]): boolean {
		if (!this.jwt) {
			return false;
		}

		if (!permissions.length) {
			return true;
		}

		permissions = permissions.filter(permission =>
			this.sessionService.get(permission) === null || this.sessionService.get(permission) === 'true'
		);

		if (!permissions.length) {
			return false;
		}

		const access = this.jwt.access;
		const missingPermissions = difference(permissions, access).length;

		return missingPermissions === 0;
	}

	hasPartialAccess(...permissions: string[]): boolean {
		if (!permissions.length) {
			return true;
		}

		const jwt = this.jwt;
		const access = jwt ? jwt.access : [];
		const missingPermissions = difference(permissions, access).length;

		return missingPermissions < permissions.length;
	}

	redirectToUrl(url: string): boolean {
		const currentRoute = url.substring(1);
		const permissions = this.permissionsByRoute.get(currentRoute);

		if (currentRoute && this.hasAccess(...permissions)) {
			return true;
		}

		for (const route of this.permissionsByRoute.routes()) {
			const permissions = this.permissionsByRoute.get(route);

			if (this.hasAccess(...permissions)) {
				this.router.navigate([`/${route}`]);
				return true;
			}
		}

		return false;
	}

	redirectToLogin(clearSession: boolean = false): void {
		let jwt = this.jwt;
		const isImpersonated = !!this.sessionService.getJwt(false)?.impersonatedBy;
		const redirectUrl = !jwt || jwt.profileId === RoleProfiles.Cardholder ? 'login' : 'login/local';
		
		if (clearSession) {
			if (isImpersonated) {
				this.sessionService.restore();
				jwt = this.jwt;
			}
			else {
				this.sessionService.clear();
				jwt = null;
			}
		}

		if (isImpersonated && !clearSession) {
			window.location.href = '';
		}
		else {
			this.router.navigate([redirectUrl]);
		}
	}

	refreshSession(): Observable<unknown> {
		if (this.isLoggingOut) {
			return of();
		}

		const isImpersonated = !!this.sessionService.getJwt(false)?.impersonatedBy;
		if (isImpersonated) {
			return of();
		}

		const accessToken = this.sessionService.getAccessToken();
		if (!accessToken) {
			return of();
		}

		return this.accountService
			.refresh({
				accessToken
			})
			.pipe(
				tap(r => {
					if (!r.accessToken) {
						throw ['Security tokens unavailable'];
					}

					// Do not overwrite a newer login, refresh, or logout.
					if (this.isLoggingOut || this.sessionService.getAccessToken() !== accessToken) {
						return;
					}

					const currentJwt = this.sessionService.getJwt(false);
					this.logger.log(`User access for ${currentJwt?.nameIdentifier ?? 'current user'} has been refreshed.`);
					this.sessionService.setAccessToken(r.accessToken);
					this.startAutoLogout();
				}),
				catchError(() => {
					if (this.isLoggingOut || this.sessionService.getAccessToken() !== accessToken) {
						return EMPTY;
					}

					if (this.jwt) {
						this.logOut();
					}
					else {
						this.sessionService.clear();
						this.redirectToLogin(true);
					}

					return EMPTY;
				})
			);
	}
}
